import { Component, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-screen bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center px-4">
      <div class="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center">Reset Password</h2>
        
        @if (!token) {
          <div class="text-center text-red-500 mb-6">
            Missing or invalid reset token. Please request a new recovery email.
          </div>
        }

        @if (token) {
          <form (ngSubmit)="onSubmit()" class="space-y-4">
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                id="password"
                [(ngModel)]="password"
                name="password"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                placeholder="Enter new password"
                required
              />
            </div>

            <div>
              <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                [(ngModel)]="confirmPassword"
                name="confirmPassword"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                placeholder="Confirm new password"
                required
              />
            </div>

            <button
              type="submit"
              [disabled]="loading"
              class="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              @if (loading) {
                Resetting...
              } @else {
                Reset Password
              }
            </button>
          </form>
        }

        <p class="mt-6 text-center">
          <a [routerLink]="['/login']" class="text-blue-500 hover:text-blue-700 font-semibold cursor-pointer">
            Back to Login
          </a>
        </p>
      </div>
    </div>
  `,
})
export class ResetPasswordComponent implements OnInit {
  password = '';
  confirmPassword = '';
  token: string | null = null;
  loading = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token');
  }

  onSubmit(): void {
    if (!this.token) {
      this.toastService.error('Invalid reset token');
      return;
    }

    if (!this.password || !this.confirmPassword) {
      this.toastService.error('Please fill in all fields');
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.toastService.error('Passwords do not match');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    this.authService.resetPassword(this.token, this.password).subscribe({
      next: (res) => {
        this.ngZone.run(() => {
          this.toastService.success('Password reset successfully! You can now log in.');
          this.router.navigate(['/login']);
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.toastService.error(err.error?.error || 'Failed to reset password');
          this.loading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }
}
